/**
 * Phone numbers for the countries Callout can call: Japan (+81) and the
 * United States / Canada (+1, one entry since they share the country code
 * and number format).
 *
 * The call form keeps the country and the national number apart; this module
 * turns what the user typed into E.164 for the backend and says what is wrong
 * with it while they type. The backend applies the same rules again, plus the
 * ones that need data it has (premium-rate and non-US/Canada +1 codes).
 */

import { CountryCode } from './market';

export interface Country {
  code: CountryCode;
  dialCode: string;
  flag: string;
  label: string;
  /** "a Japanese number" */
  adjective: string;
  placeholder: string;
  /** Shown under the field. */
  hint: string;
  /** The language the call is made in unless the user picks another. */
  defaultLanguage: 'ja' | 'en';
}

export const COUNTRIES: Record<CountryCode, Country> = {
  JP: {
    code: 'JP',
    dialCode: '+81',
    flag: '🇯🇵',
    label: 'Japan',
    adjective: 'Japanese',
    placeholder: '03-1234-5678',
    hint: 'Landline or mobile, as you would dial it in Japan (the leading 0 is fine).',
    defaultLanguage: 'ja',
  },
  US: {
    code: 'US',
    dialCode: '+1',
    flag: '🇺🇸',
    label: 'United States / Canada',
    adjective: 'US or Canadian',
    placeholder: '(415) 555-0142',
    hint: 'The 10-digit number, area code first.',
    defaultLanguage: 'en',
  },
};

export const COUNTRY_LIST: Country[] = [COUNTRIES.US, COUNTRIES.JP];

/** Digits only, with the country's own prefix removed if the user typed it. */
export function nationalDigits(country: CountryCode, input: string): string {
  let digits = input.replace(/\D/g, '');
  const dial = COUNTRIES[country].dialCode.slice(1);
  if (input.trim().startsWith('+') && digits.startsWith(dial)) {
    digits = digits.slice(dial.length);
  }
  if (country === 'JP') {
    // Dialed nationally with the trunk 0: 03-1234-5678 -> 312345678
    if (digits.startsWith('0')) digits = digits.slice(1);
  } else if (country === 'US') {
    // 1 (415) 555-0142 -> 4155550142
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  }
  return digits;
}

/** What's wrong with the number, or '' when it can be dialed. */
export function phoneProblem(country: CountryCode, digits: string): string {
  if (!digits) return 'Enter the number to call.';
  if (country === 'JP') {
    if (digits.length < 9) return 'Japanese numbers have 9 or 10 digits after the leading 0.';
    if (digits.length > 10) return 'Too long: Japanese numbers have at most 10 digits after the leading 0.';
    return '';
  }
  if (digits.length !== 10) return 'US and Canadian numbers have 10 digits (area code and number).';
  if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return "That isn't a valid US or Canadian number.";
  if (digits.startsWith('900') || digits.slice(3, 6) === '976') return "Premium-rate numbers can't be called.";
  return '';
}

/** E.164 for the backend, e.g. +14155550142 or +81312345678. */
export function toE164(country: CountryCode, digits: string): string {
  return COUNTRIES[country].dialCode + digits;
}

/** The number as people write it locally, for display. */
export function formatNational(country: CountryCode, digits: string): string {
  if (country === 'US' && digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (country === 'JP' && digits.length >= 9) {
    return `0${digits}`;
  }
  return digits;
}

/**
 * Phone numbers written in international form (+CC…) in the prompt that are
 * not from the destination country, e.g. a UK mobile given as the contact
 * number for a booking at a restaurant in Japan. Mirrors
 * mcp_server._foreign_numbers.
 */
export function foreignNumbers(country: CountryCode, text: string): string[] {
  const home = COUNTRIES[country].dialCode.slice(1);
  const matches = text.normalize('NFKC').match(/\+\s*\(?\s*\d[\d\s\-().]{5,}\d/g) ?? [];
  return matches
    .filter((m) => {
      const digits = m.replace(/\D/g, '');
      return digits.length >= 7 && !digits.startsWith(home);
    })
    .map((m) => m.trim());
}
